import { CognitoUserPoolConfig } from '@aws-amplify/core'
import { Amplify } from 'aws-amplify'
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito'
import axios, { AxiosRequestConfig } from 'axios'
import awsConfig from 'src/aws-exports.json'
import { Environments } from 'src/configurations'
import {
  getUserPoolClientIdCookieName,
  getUserPoolIdCookieName,
} from 'src/context/UserProvider/UserProvider'
import Cookies from 'universal-cookie'

export type AuthConfigResponse = {
  authType: 'password' | 'sso'
  clientId: string | null
  userPoolId: string | null
  identityProviderName: string | null
}

// Keys that should be preserved for "remember me" device functionality
const DEVICE_KEYS_TO_PRESERVE = [
  'deviceKey',
  'deviceGroupKey',
  'randomPasswordKey',
]

/**
 * Checks if a localStorage key is a device-related key that should be preserved.
 * Device keys enable "remember me" functionality across sessions.
 */
function isDeviceKey(key: string): boolean {
  return DEVICE_KEYS_TO_PRESERVE.some((deviceKey) => key.endsWith(deviceKey))
}

/**
 * Clears Amplify-related state from cookies and localStorage.
 * This prevents stale state when switching between user pools.
 * Preserves device-related keys to maintain "remember me" functionality.
 */
export function clearAmplifyState(env: Environments): void {
  const cookies = new Cookies()

  // Clear SSO user pool cookies
  cookies.remove(getUserPoolClientIdCookieName(env), { path: '/' })
  cookies.remove(getUserPoolIdCookieName(env), { path: '/' })

  // Clear Amplify localStorage entries (keys like CognitoIdentityServiceProvider.{clientId}.*)
  // but preserve device-related keys for "remember me" functionality
  try {
    const keysToRemove: string[] = []
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (
        key?.startsWith('CognitoIdentityServiceProvider') &&
        !isDeviceKey(key)
      ) {
        keysToRemove.push(key)
      }
    }
    keysToRemove.forEach((key) => localStorage.removeItem(key))
  } catch {
    // localStorage may not be available in some contexts
  }
}

/**
 * Returns the default Cognito user pool configuration.
 * Priority:
 * 1. Values from cookies (set by /authconfig API response)
 * 2. Legacy pool as fallback for backwards compatibility
 *
 * @param env - The current environment (Prod or Stage)
 */
export function getDefaultCognitoConfig(
  env: Environments,
): CognitoUserPoolConfig {
  // Local Moto override: env vars set via .env.local from seed-moto-cognito.sh output
  // Server-side: available in process.env. Client-side: injected via __INITIAL_STATE__
  const getEnvVar = (key: string): string | undefined => {
    // Server-side
    if (typeof process !== 'undefined' && process.env[key]) return process.env[key]
    // Client-side: read from SSR-injected initial state
    if (typeof window !== 'undefined') {
      const w = window as unknown as { __INITIAL_STATE__?: { session?: { envConfig?: Record<string, string> } } }
      const initialState = w.__INITIAL_STATE__
      return initialState?.session?.envConfig?.[key]
    }
    return undefined
  }
  // Security: only allow Moto override in non-prod/stage environments
  const isLocalEnv =
    env !== Environments.Prod && env !== Environments.Stage
  const motoEndpoint = isLocalEnv
    ? getEnvVar('COGNITO_USER_POOL_ENDPOINT')
    : undefined
  const motoPoolId = isLocalEnv
    ? getEnvVar('COGNITO_USER_POOL_ID_OVERRIDE')
    : undefined
  const motoClientId = isLocalEnv
    ? getEnvVar('COGNITO_USER_POOL_CLIENT_ID_OVERRIDE')
    : undefined
  if (motoEndpoint && motoPoolId && motoClientId) {
    return {
      userPoolId: motoPoolId,
      userPoolClientId: motoClientId,
      userPoolEndpoint: motoEndpoint,
    }
  }

  const cookies = new Cookies()
  const userPoolClientId = cookies.get(getUserPoolClientIdCookieName(env))
  const userPoolId = cookies.get(getUserPoolIdCookieName(env))

  // Primary: use values from cookies (from /authconfig API)
  // Note: Amplify v6 infers region from userPoolId (format: region_id)
  if (userPoolClientId && userPoolId) {
    return { userPoolClientId, userPoolId }
  }

  // Fallback: use legacy pool for backwards compatibility
  return env === Environments.Prod ? awsConfig.prod : awsConfig.stage
}

/**
 * Options for configuring Amplify with user-specific settings.
 * When provided with ssoEnabled=true and email, will call /authconfig API.
 */
export type ConfigureAmplifyOptions = {
  /** User's email address - required when ssoEnabled is true */
  email?: string
  /** Axios request config for API calls */
  axiosConfig?: AxiosRequestConfig
  /** Whether SSO feature flag is enabled */
  ssoEnabled?: boolean
}

/**
 * Configures Amplify and ensures the internal token provider is updated.
 *
 * Amplify v6 has a bug where `Amplify.configure()` only propagates the Auth
 * config to the internal CognitoUserPoolsTokenProvider on the FIRST call.
 * Subsequent calls update the global config but not the token provider's
 * TokenStore, causing tokens to be stored/retrieved under the wrong
 * userPoolClientId key prefix. This wrapper ensures the token provider
 * always receives the latest config.
 */
export function configureAmplifyWithTokenProvider(
  cognitoConfig: CognitoUserPoolConfig,
): void {
  Amplify.configure({
    Auth: {
      Cognito: cognitoConfig,
    },
  })
  cognitoUserPoolsTokenProvider.setAuthConfig({
    Cognito: cognitoConfig,
  })
}

/**
 * Unified function to configure AWS Amplify for authentication.
 *
 * Behavior:
 * - If ssoEnabled=true and email is provided: calls /authconfig API to get
 *   the correct user pool configuration (SSO or password flow)
 * - Otherwise: uses default configuration (cookies or legacy fallback)
 *
 * This is the single source of truth for all Amplify configuration in the app.
 *
 * @param env - The current environment (Prod or Stage)
 * @param options - Optional configuration for user-specific setup
 * @returns AuthConfigResponse if /authconfig was called, null otherwise
 */
export async function configureAmplify(
  env: Environments,
  options?: ConfigureAmplifyOptions,
): Promise<AuthConfigResponse | null> {
  const { email, axiosConfig, ssoEnabled } = options || {}

  // If Moto override is active, always use it — skip /authconfig entirely
  const motoConfig = getDefaultCognitoConfig(env)
  if (motoConfig.userPoolEndpoint) {
    configureAmplifyWithTokenProvider(motoConfig)
    return null
  }

  // If SSO is enabled and we have an email, call /authconfig to get user-specific config
  if (ssoEnabled && email && axiosConfig) {
    // Clear existing state to prevent stale config issues
    clearAmplifyState(env)

    const response = await axios.post<AuthConfigResponse>(
      '/authconfig',
      { email },
      axiosConfig,
    )
    const authConfig = response.data

    let cognitoConfig: CognitoUserPoolConfig

    // Use pool info from API if provided, otherwise use legacy fallback
    if (authConfig.userPoolId && authConfig.clientId) {
      cognitoConfig = {
        userPoolId: authConfig.userPoolId,
        userPoolClientId: authConfig.clientId,
      }

      // Store in cookies so getDefaultCognitoConfig picks it up on subsequent calls
      const cookies = new Cookies()
      cookies.set(
        getUserPoolClientIdCookieName(env),
        cognitoConfig.userPoolClientId,
        { path: '/' },
      )
      cookies.set(getUserPoolIdCookieName(env), cognitoConfig.userPoolId, {
        path: '/',
      })
    } else {
      // API didn't return pool info, use legacy fallback
      cognitoConfig =
        env === Environments.Prod ? awsConfig.prod : awsConfig.stage
    }

    // Configure Amplify with the resolved config
    configureAmplifyWithTokenProvider(cognitoConfig)

    return authConfig
  }

  // SSO not enabled or no email - use default configuration
  const cognitoConfig = getDefaultCognitoConfig(env)
  configureAmplifyWithTokenProvider(cognitoConfig)

  return null
}
